import React, { useState, useEffect, useCallback } from 'react';

const ContractorView = ({ apiUrl, token, activeTab }) => {
    // --- Estados de Datos ---
    const [ots, setOts] = useState([]);
    const [stock, setStock] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- Estados para Formularios ---
    const [otForm, setOtForm] = useState({ desc: '', ubic: 'POLIDEPORTIVO' }); // --- CAMBIO: Valor por defecto para ubicación
    const [otItems, setOtItems] = useState([]);
    const [stockFilters, setStockFilters] = useState({ id: '', desc: '' });

    // --- Estados para Funcionalidades de IA ---
    const [isAiLoading, setIsAiLoading] = useState(false);
    const [whatsappMessage, setWhatsappMessage] = useState('');

    // --- Lógica de Obtención de Datos ---
    const fetchData = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const headers = { 'Authorization': `Bearer ${token}` };
            const [otsRes, stockRes] = await Promise.all([
                fetch(`${apiUrl}/api/getSheetData?sheetName=OTs`, { headers }),
                fetch(`${apiUrl}/api/getSheetData?sheetName=Stock`, { headers })
            ]);
            const otsData = await otsRes.json();
            const stockData = await stockRes.json();
            setOts(otsData.slice(1).map((row, index) => ({ data: row, index: index + 2 })));
            setStock(stockData.slice(1));
        } catch (err) {
            console.error("Error fetching data:", err);
            setError("No se pudieron cargar los datos. Revisa tu conexión o intenta recargar la página.");
        } finally {
            setLoading(false);
        }
    }, [apiUrl, token]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // --- Lógica de Creación de OT ---
    const handleCreateOT = async (e) => {
        e.preventDefault();
        if (!otForm.desc || !otForm.ubic || otItems.some(item => !item.description || !item.qty)) {
            alert("Complete todos los campos de la OT y al menos un consumible.");
            return;
        }
        const otId = `OT-${new Date().toISOString().replace(/[-:T.Z]/g, '')}`;
        const fecha = new Date().toLocaleString('es-AR');
        const newRow = [otId, fecha, "Contratista", otForm.desc, otForm.ubic, "Pendiente"];
        await fetch(`${apiUrl}/api/appendRow`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ sheetName: 'OTs', rowData: newRow }) });
        
        for (const item of otItems) {
            const movId = `MOV-${new Date().toISOString().replace(/[-:T.Z]/g, '')}`;
            const movRow = [movId, new Date().toLocaleString('es-AR'), otId, item.id, "Solicitud", item.qty, "Contratista", "pending"];
            await fetch(`${apiUrl}/api/appendRow`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ sheetName: 'Movimientos', rowData: movRow }) });
        }

        alert(`OT ${otId} creada con ${otItems.length} consumibles.`);
        setOtForm({ desc: '', ubic: 'POLIDEPORTIVO' });
        setOtItems([]);
        setWhatsappMessage('');
        fetchData();
    };

    // --- Lógica de Gestión de Ítems en la OT ---
    const addItemToOt = () => setOtItems([...otItems, { id: '', description: '', qty: '' }]);
    const removeItemFromOt = (index) => setOtItems(otItems.filter((_, i) => i !== index));
    const updateOtItem = (index, field, value) => {
        const newItems = [...otItems];
        newItems[index][field] = value;
        setOtItems(newItems);
    };

    // --- Lógica de IA para WhatsApp ---
    const handleParseMessage = async () => {
        if (!whatsappMessage) {
            alert("Por favor, pega un mensaje de WhatsApp primero.");
            return;
        }
        setIsAiLoading(true);
        try {
            const response = await fetch(`${apiUrl}/api/parse-whatsapp-message`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ message: whatsappMessage }),
            });
            const data = await response.json();
            if (response.ok) {
                // --- CAMBIO: Autocompletar la descripción de la OT con la tarea encontrada ---
                const firstItemDescription = data.items.length > 0 ? data.items[0].description : '';
                setOtForm(prev => ({ ...prev, desc: `Trabajo basado en mensaje: ${firstItemDescription.substring(0, 50)}...` }));
                
                // Añadir ítems encontrados y sugeridos a la lista de la OT
                const processedItems = data.items.map(item => ({ ...item, qty: item.qty.toString() }));
                setOtItems(prev => [...prev, ...processedItems]);
            } else {
                alert(`Error de IA: ${data.error}`);
            }
        } catch (err) {
            console.error(err);
            alert("No se pudo conectar con el servicio de IA.");
        } finally {
            setIsAiLoading(false);
        }
    };
    
    // --- Lógica de Stock y Filtros ---
    const handleStockFilterChange = (field, value) => {
        setStockFilters({ ...stockFilters, [field]: value });
    };
    const filteredStock = stock.filter(item =>
        item[0].toLowerCase().includes(stockFilters.id.toLowerCase()) &&
        item[1].toLowerCase().includes(stockFilters.desc.toLowerCase())
    );

    // --- Renderizado Condicional ---
    if (loading) return <p>Cargando...</p>;
    if (error) return <div className="card"><h3>Error</h3><p>{error}</p></div>;

    switch (activeTab) {
        case 'Generar OT':
            return (
                <div className="card">
                    <h3>Crear Nueva Orden de Trabajo desde Mensaje</h3>
                    <textarea
                        placeholder="Pega aquí el mensaje de WhatsApp..."
                        value={whatsappMessage}
                        onChange={(e) => setWhatsappMessage(e.target.value)}
                        rows="5"
                        style={{ width: '100%', marginBottom: '10px' }}
                    />
                    <button type="button" onClick={handleParseMessage} disabled={isAiLoading} className="secondary">
                        {isAiLoading ? '🤖 Analizando...' : '🤖 Analizar Mensaje con IA'}
                    </button>
                    <hr />
                    <h4>Ítems para esta OT</h4>
                    {otItems.length === 0 ? <p>Analiza un mensaje para añadir ítems.</p> : (
                        otItems.map((item, index) => (
                            <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center', padding: '5px', border: `1px solid ${item.id ? '#ccc' : 'red'}`, borderRadius: '5px' }}>
                                <input type="text" value={item.description || ''} onChange={(e) => updateOtItem(index, 'description', e.target.value)} placeholder="Descripción" style={{flex: 2}} />
                                <input type="text" value={item.id || 'Pendiente ID'} readOnly placeholder="ID" style={{flex: 1}}/>
                                <input type="number" value={item.qty} onChange={(e) => updateOtItem(index, 'qty', e.target.value)} required placeholder="Cant." style={{flex: 0.5}}/>
                                {otItems.length > 1 && <button type="button" className="danger" onClick={() => removeItemFromOt(index)}>-</button>}
                                {/* --- CAMBIO: Indicador visual del origen del ítem --- */}
                                <span style={{fontSize: '0.8em', color: '#666'}}>
                                    {item.id ? '📦 Encontrado' : '✨ Creado'}
                                </span>
                            </div>
                        ))
                    )}
                    <button type="button" onClick={addItemToOt}>+ Añadir otro consumible manualmente</button>
                    <hr />
                    <form onSubmit={handleCreateOT}>
                        {/* --- CAMBIO: Input de descripción ahora es editable y se autocompleta --- */}
                        <input type="text" placeholder="Descripción del trabajo" value={otForm.desc} onChange={(e) => setOtForm({ ...otForm, desc: e.target.value })} required />
                        {/* --- CAMBIO: Select para la ubicación --- */}
                        <select value={otForm.ubic} onChange={(e) => setOtForm({ ...otForm, ubic: e.target.value })} required>
                            <option value="POLIDEPORTIVO">Polideportivo</option>
                            <option value="PARQUE">Parque</option>
                            <option value="MINELA">Minela</option>
                        </select>
                        <button type="submit">Crear OT</button>
                    </form>
                </div>
            );
        case 'Mis Solicitudes':
            return (
                <div className="card">
                    <h3>Mis Órdenes de Trabajo</h3>
                    {ots.length === 0 ? <p>No has creado ninguna OT aún.</p> : (
                        <div className="table-container">
                            <table>
                                <thead>
                                    <tr>
                                        <th>ID OT</th>
                                        <th>Fecha</th>
                                        <th>Descripción</th>
                                        <th>Ubicación</th>
                                        <th>Estado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ots.map(ot => (
                                        <tr key={ot.data[0]}>
                                            <td>{ot.data[0]}</td>
                                            <td>{ot.data[1]}</td>
                                            <td>{ot.data[3]}</td>
                                            <td>{ot.data[4]}</td>
                                            <td>{ot.data[5]}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            );
        case 'Ver Stock':
            return (
                <div className="card">
                    <h3>Stock Actual de Consumibles</h3>
                    <div className="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th><input type="text" placeholder="Filtrar por ID..." value={stockFilters.id} onChange={(e) => handleStockFilterChange('id', e.target.value)} /></th>
                                    <th><input type="text" placeholder="Filtrar por Descripción..." value={stockFilters.desc} onChange={(e) => handleStockFilterChange('desc', e.target.value)} /></th>
                                    <th>Cantidad</th>
                                    <th>Unidad</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredStock.map(item => <tr key={item[0]}><td>{item[0]}</td><td>{item[1]}</td><td>{item[2]}</td><td>{item[3]}</td></tr>)}
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        default:
            return null;
    }
};

export default ContractorView;